import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { AuditLog, Folder } from '@/types/api';

interface AuditEntry extends AuditLog {
  actor_name: string | null;
}

export function useFolder(folderId: string | null | undefined) {
  return useQuery({
    enabled: !!folderId,
    queryKey: ['folders', 'detail', folderId],
    queryFn: async () => (await api.get<Folder>(`/folders/${folderId}`)).data,
  });
}

export function useFolders(
  workspaceId: string | null | undefined,
  includeArchived = false,
) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ['folders', workspaceId, { includeArchived }],
    queryFn: async () =>
      (
        await api.get<Folder[]>(`/folders`, {
          params: {
            workspace_id: workspaceId,
            include_archived: includeArchived ? true : undefined,
          },
        })
      ).data,
  });
}

export function useCreateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      workspace_id: string;
      parent_id: string | null;
      name: string;
    }) => {
      const { data } = await api.post<Folder>('/folders', vars);
      return data;
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['folders', vars.workspace_id] }),
  });
}

export function useRenameFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; name: string; workspace_id: string }) => {
      const { data } = await api.patch<Folder>(`/folders/${vars.id}`, { name: vars.name });
      return data;
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['folders', vars.workspace_id] }),
  });
}

export function useDeleteFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; workspace_id: string }) => {
      await api.delete(`/folders/${vars.id}`);
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['folders', vars.workspace_id] }),
  });
}

export function useArchiveFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; workspace_id: string }) => {
      const { data } = await api.patch<Folder>(`/folders/${vars.id}`, { is_archived: true });
      return data;
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['folders', vars.workspace_id] }),
  });
}

export function useUnarchiveFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; workspace_id: string }) => {
      const { data } = await api.patch<Folder>(`/folders/${vars.id}`, { is_archived: false });
      return data;
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['folders', vars.workspace_id] }),
  });
}

/**
 * Update a folder's `sort_order` — used by drag-and-drop reordering inside
 * the FolderTree.
 */
export function useReorderFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; sort_order: number; workspace_id: string }) => {
      const { data } = await api.patch<Folder>(`/folders/${vars.id}`, {
        sort_order: vars.sort_order,
      });
      return data;
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['folders', vars.workspace_id] }),
  });
}

/**
 * Re-parent a folder — used when a folder is dragged onto another folder in
 * the tree to nest it.
 */
export function useMoveFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; parent_id: string; workspace_id: string }) => {
      const { data } = await api.patch<Folder>(`/folders/${vars.id}`, {
        parent_id: vars.parent_id,
      });
      return data;
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['folders', vars.workspace_id] }),
  });
}

export interface FolderSubscriptionStatus {
  subscribed: boolean;
}

export function useFolderSubscription(folderId: string | null | undefined) {
  return useQuery({
    enabled: !!folderId,
    queryKey: ['folder-subscription', folderId],
    queryFn: async () =>
      (await api.get<FolderSubscriptionStatus>(`/folders/${folderId}/subscription`)).data,
  });
}

export function useSubscribeFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { folder_id: string }) => {
      const { data } = await api.post<FolderSubscriptionStatus>(
        `/folders/${vars.folder_id}/subscribe`,
      );
      return data;
    },
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ['folder-subscription', vars.folder_id] }),
  });
}

export function useUnsubscribeFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { folder_id: string }) => {
      const { data } = await api.delete<FolderSubscriptionStatus>(
        `/folders/${vars.folder_id}/subscribe`,
      );
      return data;
    },
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ['folder-subscription', vars.folder_id] }),
  });
}

export function useFolderActivity(folderId: string | null | undefined) {
  return useQuery({
    enabled: !!folderId,
    queryKey: ['folders', 'activity', folderId],
    queryFn: async () =>
      (await api.get<{ items: AuditEntry[]; total: number }>(`/folders/${folderId}/activity`)).data,
  });
}
